import { InternalRefetchQueriesInclude } from '@apollo/client/core/types'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { GetCustomerDispensaryPromosQuery } from '../../../../../graphql/camel'
import Spacer from '../../../../Spacer'
import Spinner from '../../../../Spinner'
import DispensaryAddNewPromo from './DispensaryAddNewPromo'
import DispensaryPromoTable from './DispensaryPromoTable'
import { StyledHeader } from './styled'
import { CustomerDispensaryPromos, MutatePromoProps } from './types'

const existingCodes = (promos: CustomerDispensaryPromos): string[] =>
  promos.reduce((acc: string[], promo) => {
    if (!promo || promo.inactive) return acc
    acc.push(promo.dispensaryCode.toUpperCase())
    return acc
  }, [])

const existingOneTimeOnlyIds = (promos: CustomerDispensaryPromos): string[] =>
  promos.reduce((acc: string[], promo) => {
    if (!promo || promo.inactive) return acc
    // TODO: revisit for stability
    if (promo.rootPromoName.includes('One Time Only')) {
      acc.push(promo.rootPromoId)
    }
    return acc
  }, [])

const StyledEmptyMessage = styled.div`
  margin-top: 32px;
  line-height: 30px;
  text-align: center;
`

export const StyledSmallParagraph = styled.p`
  font-size: 12px;
`

export interface DispensaryPromosPageContentProps {
  data: GetCustomerDispensaryPromosQuery | undefined
  dispensarySlug: string
  loading: boolean
  refetchQueries: InternalRefetchQueriesInclude
}

const DispensaryPromosPageContent: React.VFC<DispensaryPromosPageContentProps> = ({
  data,
  dispensarySlug,
  loading,
  refetchQueries,
}) => {
  const derived = useMemo(():
    | { mutatePromoProps: MutatePromoProps; promos: CustomerDispensaryPromos }
    | undefined => {
    const rootNode = data?.getCustomerDispensaryPromos
    return rootNode
      ? {
          mutatePromoProps: {
            dispensarySlug,
            existingCodes: existingCodes(rootNode.promos),
            existingOneTimeOnlyIds: existingOneTimeOnlyIds(rootNode.promos),
            rootPromos: rootNode.rootPromos,
            refetchQueries,
          },
          promos: rootNode.promos,
        }
      : undefined
  }, [data?.getCustomerDispensaryPromos, dispensarySlug, refetchQueries])

  return (
    <>
      <StyledHeader>
        <h3>Dispensary Promotions</h3>
      </StyledHeader>
      {!!derived && (
        <>
          <DispensaryAddNewPromo {...derived.mutatePromoProps} />
          {derived.promos.length > 0 ? (
            <DispensaryPromoTable
              promos={derived.promos}
              mutatePromoProps={derived.mutatePromoProps}
            />
          ) : (
            <StyledEmptyMessage>
              No promotions added yet. Use the form above to get started.
            </StyledEmptyMessage>
          )}
          <Spacer size={4} />
          <StyledSmallParagraph>
            When using this option for your dispensary promo codes, please be mindful of the
            compliance guidelines that are strictly enforced when providing these codes to your
            patients. The dispensary promo codes created herein are for patient use only and will
            only work on your dispensary. Dispensary promo codes cannot be shared on websites, nor
            social channels; however, they can be shared in private communications with patients. If
            you have any questions or require unique criteria for a dispensary promo code, contact
            your assigned sales rep for assistance.
          </StyledSmallParagraph>
        </>
      )}
      <Spinner loading={loading} />
    </>
  )
}

export default DispensaryPromosPageContent
